import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { refreshTokenReq, signUpReq, signinReq } from "../../apis/userApi";
import { generateRandomId } from "../../helpers/string";

// Define the async thunk for signin
export const signin = createAsyncThunk(
  "auth/signin",
  async ({ loginData, rememberMe }, { rejectWithValue }) => {
    try {
      const response = await signinReq(loginData);
      const af_device_id = localStorage.getItem("af-device-id");
      !af_device_id && localStorage.setItem("af-device-id", generateRandomId());
      return { data: response, rememberMe: rememberMe };
    } catch (error) {
      return rejectWithValue(error.response);
    }
  }
);

export const signup = createAsyncThunk(
  "auth/signup",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await signUpReq(payload);
      const af_device_id = localStorage.getItem("af-device-id");
      !af_device_id && localStorage.setItem("af-device-id", generateRandomId());
      return response;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  }
);
export const refreshTokenAuth = createAsyncThunk(
  "auth/refreshToken",
  async (refreshToken, thunkAPI) => {
    try {
      const response = await refreshTokenReq(refreshToken);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response);
    }
  }
);

// Create a slice with createSlice
const authSlice = createSlice({
  name: "auth",
  initialState: {
    isLoading: false,
    isError: false,
    idToken: null,
    isAuthenticated: false,
    refreshToken: null,
    rememberMe: false,
    customLoading: false,
    contractId: null,
    userId: null,
    email: "",
    name: "",
    contact: "",
  },
  reducers: {
    deAuthenticate: (state) => {
      state.isAuthenticated = false;
      state.idToken = null;
      state.refreshToken = null;
      state.rememberMe = false;
      state.contractId = null;
      state.userId = null;
      state.email = null;
      state.name = null;
      state.contact = null;
    },
    setCustomLoading: (state) => {
      state.customLoading = !state.customLoading;
    },
    setContractId: (state, action) => {
      state.contractId = action.payload;
    },
    setContact: (state, action) => {
      state.contact = action.payload;
    },
    removedContractId: (state, action) => {
      state.contractId = null;
    },
    setGoogleAuth: (state, action) => {
      state.isLoading = false;
      state.idToken = action.payload.idToken;
      state.contact = action.payload.contact;
      state.refreshToken = action.payload.refreshToken;
      state.isAuthenticated = true;
      state.userId = action.payload.uid;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signin.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(signin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.idToken = action.payload.data.idToken;
        state.contact = action.payload.data.contact;
        state.refreshToken = action.payload.data.refreshToken;
        state.isAuthenticated = action.payload.data.requires_2fa ? false : true;
        state.rememberMe = action.meta.arg.rememberMe; // Access rememberMe from action meta
        state.userId = action.payload.data.id;
        state.email = action.payload.data.email;
        state.name = action.payload.data.fullname;
      })
      .addCase(signin.rejected, (state) => {
        state.isLoading = false;
        state.isError = true;
      })
      .addCase(signup.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(signup.fulfilled, (state, action) => {
        state.userId = action.payload.id;
        state.isLoading = false;
        state.idToken = action.payload.idToken;
        state.contact = action.payload.contact;
        state.refreshToken = action.payload.refreshToken;
        state.isAuthenticated = true;
      })
      .addCase(signup.rejected, (state) => {
        state.isLoading = false;
        state.isError = true;
      })
      .addCase(refreshTokenAuth.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(refreshTokenAuth.fulfilled, (state, action) => {
        state.isLoading = false;
        state.idToken = action.payload.idToken;
        state.contact = action.payload.contact;
        state.refreshToken = action.payload.refreshToken;
        state.isAuthenticated = true;
      })
      .addCase(refreshTokenAuth.rejected, (state) => {
        state.isLoading = false;
        state.isError = true;
      });
  },
});

// Export the async thunk and any reducers if needed
export const {
  deAuthenticate,
  setCustomLoading,
  removedContractId,
  setContractId,
  setGoogleAuth,
  setContact,
} = authSlice.actions;
export default authSlice.reducer;
